import { ActivatedRoute, Router } from '@angular/router';
import { Component, ElementRef, OnInit, QueryList, VERSION, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { AuthService } from '../../core/services/auth.service';
import { DataService } from 'src/app/core/services/data.service';
import { HttpResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastrService } from 'ngx-toastr';
import { XmppService } from 'src/app/core/services/xmpp.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css', '../../shared/styles/common.css'],
    standalone: false
})
export class LoginComponent implements OnInit {
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  @ViewChild('otp1') otp1Input!: ElementRef;

  loginForm: FormGroup;
  isLoading = false;
  hidePassword = false;
  get_login_json_data: any;

  // currentApplicationVersion = environment.appVersion;
  submitted = false;
  password_status = "fa fa-eye-slash";
  hide = true;
  login_details: any;
  version = VERSION.full;
  message: string = '';
  subscription: any;

  // OTP related properties
  useOTP: boolean = false;
  otpSent: boolean = false;
  currentEmailOrMobile: string = '';
  loginType: 'email' | 'mobile' = 'email';
  otpDigits: string[] = ['', '', '', '', '', ''];
  resendTimer: number = 60;
  canResend: boolean = false;
  private timerInterval: any;

  constructor(
    private fb: FormBuilder, 
    private authService: AuthService, 
    private data: DataService, 
    private router: Router,private route: ActivatedRoute,
    private snackBar: MatSnackBar, 
    private toastr: ToastrService,
    private xmppService: XmppService, 
  ) {
    this.loginForm = this.fb.group({
      emailOrMobile: ['', [Validators.required]],
      password: [''],
      otp: [''],
      rememberMe: [false] 
    });
  }

  ngOnInit(): void {
    // let checkout = localStorage.getItem('checkoutLog');
    // let getToken = sessionStorage.getItem('TOKEN')

    // if (!getToken) {
    //   this.toggleLoginMethod();
    //   return;
    // }
    //this.toggleLoginMethod();

    const resetSuccess = this.route.snapshot.queryParams['resetSuccess'];
    const email = this.route.snapshot.queryParams['email'];

    if (resetSuccess) {
      this.useOTP = false;
      this.loginForm.get('password')?.setValidators([Validators.required]);
      this.loginForm.get('emailOrMobile')?.setValidators([
          Validators.required,
          this.validateEmailOrMobile.bind(this)
      ]);

      this.loginForm.get('otp')?.clearValidators();

      this.loginForm.get('password')?.updateValueAndValidity();
      this.loginForm.get('emailOrMobile')?.updateValueAndValidity();
      this.loginForm.get('otp')?.updateValueAndValidity();

      this.loginForm.patchValue({
        emailOrMobile: email || ''
      });
    } else {
      this.toggleLoginMethod();
      if (email) {
        this.loginForm.patchValue({
          emailOrMobile: email
        });
      }
    }

    const c = this.loginForm.value;
    c.rememberMe = false;
    this.loginForm.patchValue(c);

    const rememberedEmailOrMobile = localStorage.getItem('remembered_email_or_mobile');
    const rememberedPassword = localStorage.getItem('remembered_password');
    const rememberedChecked = localStorage.getItem('remembered_checked');

    if (rememberedChecked && rememberedEmailOrMobile && rememberedPassword) {
      this.loginForm.patchValue({
        emailOrMobile: rememberedEmailOrMobile,
        password: rememberedPassword,
        rememberMe: true
      });
    }

    if (this.authService.isLoggedIn()) {
      console.log('User already logged in, redirecting to calendar...');
      this.router.navigate(['/consumer/appointments']);
    }
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  get f() { return this.loginForm.controls; }

  validateEmailOrMobile(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const mobilePattern = /^[0-9]{10,15}$/;

    if (emailPattern.test(value) || mobilePattern.test(value)) {
      return null;
    }

    return { invalidEmailOrMobile: true };
  }

  detectLoginType(value: string): 'email' | 'mobile' {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(value) ? 'email' : 'mobile';
  }

  toggleLoginMethod() {
    this.useOTP = !this.useOTP;
    this.otpSent = false;
    this.submitted = false;
    this.resetOtpInputs();

    if (this.useOTP) {
      this.loginForm.get('password')?.clearValidators();
      this.loginForm.get('emailOrMobile')?.setValidators([Validators.required, this.validateEmailOrMobile.bind(this)]);
      this.loginForm.get('otp')?.setValidators([Validators.required, Validators.pattern(/^\d{6}$/)]);
    } else {
      this.loginForm.get('password')?.setValidators([Validators.required]);
      this.loginForm.get('emailOrMobile')?.setValidators([Validators.required, this.validateEmailOrMobile.bind(this)]);
      this.loginForm.get('otp')?.clearValidators();
    }

    this.loginForm.get('password')?.updateValueAndValidity();
    this.loginForm.get('emailOrMobile')?.updateValueAndValidity();
    this.loginForm.get('otp')?.updateValueAndValidity();
  }

  cancelOTP() {
    this.otpSent = false;
    this.useOTP = false;
    this.submitted = false;
    this.resetOtpInputs();
    this.loginForm.patchValue({ otp: '' });
    this.stopResendTimer();

    this.toggleLoginMethod();

    this.loginForm.get('password')?.setValidators([Validators.required]);
    this.loginForm.get('emailOrMobile')?.setValidators([Validators.required, this.validateEmailOrMobile.bind(this)]);
    this.loginForm.get('otp')?.clearValidators();
    this.loginForm.get('password')?.updateValueAndValidity();
    this.loginForm.get('emailOrMobile')?.updateValueAndValidity();
    this.loginForm.get('otp')?.updateValueAndValidity();
  }

  startResendTimer() {
    this.resendTimer = 60;
    this.canResend = false;
    
    this.timerInterval = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) {
        this.canResend = true;
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  stopResendTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.resendTimer = 60;
    this.canResend = false;
  }

  resendOTP() {
    if (!this.canResend) return;
    
    this.submitted = false;
    this.resetOtpInputs();
    const emailOrMobile = this.loginForm.value['emailOrMobile'];
    const loginType = this.detectLoginType(emailOrMobile);

    this.authService.login(emailOrMobile, '', loginType, 'login').subscribe((res: HttpResponse<any>) => {
      if (res.body?.otp_sent) {
        const destination = this.loginType === 'email' ? 'email' : 'mobile';
        this.toastr.success(res.body.msg || `OTP resent to your ${destination}`, 'Success!');
        this.startResendTimer();
      } else {
        this.toastr.error('Failed to resend OTP', 'Error!');
      }
    }, error => {
      this.toastr.error('Failed to resend OTP', 'Error!');
    });
  }

  resetOtpInputs() {
    this.otpDigits = ['', '', '', '', '', ''];
  }

onOtpDigitInput(event: Event, index: number) {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^0-9]/g, '');

  // Keep only the last typed character
  if (value.length > 1) {
    value = value.slice(-1);
  }

  // Prevent any duplicate by clearing and setting
  input.value = '';
  
  // Update model AFTER clearing input
  this.otpDigits[index] = value;
  
  // Set the input value to the single digit
  input.value = value;

  // Update form control
  const otpValue = this.otpDigits.join('');
  this.loginForm.patchValue({ otp: otpValue }, { emitEvent: false });

  // Move focus to next field if we have a digit
  if (value.length === 1 && index < 5) {
    setTimeout(() => {
      const nextInput = this.otpInputs.toArray()[index + 1]?.nativeElement;
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }, 0);
  }
}

  /**
   * Backspace/arrow navigation for the OTP boxes (`index` is 1-based).
   * One backspace always deletes one digit *and* steps the cursor back, so
   * clearing the code one box at a time doesn't leave the cursor parked on
   * the box that was just emptied.
   */
  onOtpKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    const prev = input.previousElementSibling as HTMLInputElement | null;
    const next = input.nextElementSibling as HTMLInputElement | null;

    if (event.key === 'Backspace') {
      event.preventDefault();

      // Delete this box's digit, or the previous one when this box is empty.
      if (this.otpDigits[index - 1]) {
        this.otpDigits[index - 1] = '';
        input.value = '';
      } else if (prev) {
        this.otpDigits[index - 2] = '';
        prev.value = '';
      }

      this.loginForm.patchValue({ otp: this.otpDigits.join('') });
      prev?.focus();
    } else if (event.key === 'ArrowLeft' && prev) {
      event.preventDefault();
      prev.focus();
    } else if (event.key === 'ArrowRight' && next) {
      event.preventDefault();
      next.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text') || '';
    const digits = pasteData.replace(/\D/g, '').slice(0, 6);

    for (let i = 0; i < digits.length; i++) {
      this.otpDigits[i] = digits[i];
    }

    const otpValue = this.otpDigits.join('');
    this.loginForm.patchValue({ otp: otpValue }, { emitEvent: false });

    setTimeout(() => {
      const inputs = this.otpInputs.toArray();
      const lastFilledIndex = Math.min(digits.length - 1, 5);
      if (inputs[lastFilledIndex]) {
        inputs[lastFilledIndex].nativeElement.focus();
      }
    }, 0);
  }

  passwordchange() {
    this.hidePassword = !this.hidePassword;
  }

  // onLoginButtonClicked(): void {
  //   this.submitted = true;

  //   // Validate form based on login method
  //   if (this.useOTP && !this.otpSent) {
  //     // First step: just validate email/mobile
  //     if (this.loginForm.get('emailOrMobile')?.invalid) {
  //       return;
  //     }
  //   } else if (this.useOTP && this.otpSent) {
  //     // Second step: validate both email/mobile and OTP
  //     if (this.loginForm.get('emailOrMobile')?.invalid || this.loginForm.get('otp')?.invalid) {
  //       return;
  //     }
  //   } else {
  //     // Password login: validate entire form
  //     if (this.loginForm.invalid) {
  //       return;
  //     }
  //   }

  //   const emailOrMobile = this.loginForm.value['emailOrMobile'];
  //   const password = this.loginForm.value['password'] || '';
  //   const otp = this.loginForm.value['otp'] || '';
  //   const rememberMe = this.loginForm.value['rememberMe'];

  //   // Detect login type
  //   this.loginType = this.detectLoginType(emailOrMobile);
  //   this.currentEmailOrMobile = emailOrMobile;

  //   let loginPassword = '';
  //   let loginOTP = '';

  //   if (this.useOTP) {
  //     loginOTP = this.otpSent ? otp : '';
  //   } else {
  //     loginPassword = password;
  //   }

  //   let checkout = localStorage.getItem('checkoutLog');
  //   let getToken = sessionStorage.getItem('TOKEN');

  //   this.authService.login(emailOrMobile, loginPassword, this.loginType, 'login', loginOTP)
  //     .subscribe(
  //       (res: HttpResponse<any>) => {
  //         console.log('Login response:', res);

  //         if (res.body?.otp_sent) {
  //           this.otpSent = true;
  //           this.submitted = false;
  //           const destination = this.loginType === 'email' ? 'email' : 'mobile';
  //           this.toastr.success(res.body.msg || `OTP sent to your ${destination}`, 'Success!');
            
  //           this.startResendTimer();
          
  //           // Focus first OTP input after a short delay
  //           setTimeout(() => {
  //             const firstInput = this.otpInputs?.toArray()[0];
  //             if (firstInput) {
  //               firstInput.nativeElement.focus();
  //             }
  //           }, 100);
  //           return;
  //         }

  //         if (res.body.error) {
  //           this.router.navigate(['/login']);
  //           return;
  //         }
  //         else{
  //           this.toastr.error(res.body.msg || 'Login failed', 'Error!');
  //         }

  //         console.log('Logged in successfully');
  //         console.log('res body data:', res.body.data);
  //         console.log('checkout value:', checkout);
        
  //         localStorage.setItem('TOKEN', res.body.data.token);
  //         localStorage.setItem("PK", res.body.data.pk);
  //         localStorage.setItem("first_name", res.body.data.first_name);
  //         localStorage.setItem("last_name", res.body.data.last_name)
  //         localStorage.setItem("email", res.body.data.email)

  //         if (checkout && getToken) {
  //           // this.router.navigate(['/checkout']);
  //           // return;

  //           const redirect = this.route.snapshot.queryParams['redirect'];
  //           if (redirect === 'checkout') {
  //             this.router.navigate(['/checkout']);
  //           } else {
  //             // this.router.navigate(['/home']);
  //             this.router.navigate(['/appointments']);
  //           }
  //           return
  //         }
  //         else if (getToken) {
  //           this.router.navigate(['/appointments']);
  //         }


  //       // if (!res.body.error && res.body.data?.email_verified) {
  //       //   this.authService.create_user_vitacape_consumer(
  //       //     this.get_login_json_data.data.pk + "@digicoffer.com", 
  //       //     this.get_login_json_data.token.slice(0, 20), 
  //       //     this.get_login_json_data.data.first_name, 
  //       //     this.get_login_json_data.data.pk
  //       //   ).subscribe(resp => {
  //       //     this.router.navigate(['/appointments']);
  //       //   });

  //       //   if (rememberMe && !this.useOTP) {
  //       //     localStorage.setItem('remembered_email_or_mobile', emailOrMobile);
  //       //     localStorage.setItem('remembered_password', password);
  //       //     localStorage.setItem('remembered_checked', 'true');
  //       //   } else {
  //       //     localStorage.removeItem('remembered_email_or_mobile');
  //       //     localStorage.removeItem('remembered_password');
  //       //     localStorage.removeItem('remembered_checked');
  //       //   }
  //       // } else if (!res.body.error && !res.body.data?.email_verified) {
  //       //   this.data.changeMessage(emailOrMobile);
  //       //   this.authService.resend_token(this.message).subscribe((respons: any) => {
  //       //     console.log(respons);
  //       //   });
  //       //   this.router.navigate(['/mail-verification']);
  //       // } else {
  //       //   if (res.body.msg?.email) {
  //       //     this.toastr.error(res.body.msg.email, 'Error!');
  //       //   } else {
  //       //     this.toastr.error(res.body.msg, 'Error!');
  //       //   }
  //       // }
  //     },
  //     error => {
  //       this.toastr.error('Login failed. Please try again.', 'Error!');
  //       console.error('Login error:', error);
  //     }
  //   );
  // }

  onLoginButtonClicked(): void {
    this.trimFormFields();
    this.submitted = true;

    if (!this.isFormValid()) {
      return;
    }

    const {
      emailOrMobile,
      password,
      otp,
      rememberMe
    } = this.loginForm.value;

    this.loginType = this.detectLoginType(emailOrMobile);
    this.currentEmailOrMobile = emailOrMobile;

    const { loginPassword, loginOTP } = this.prepareCredentials(password, otp);

    this.authService
      .login(emailOrMobile, loginPassword, this.loginType, 'login', loginOTP)
      .subscribe({
        next: (res: HttpResponse<any>) => this.handleLoginResponse(res, rememberMe, password, emailOrMobile),
        error: (err) => this.handleLoginError(err)
      });
  }
  
  private isFormValid(): boolean {
    if (this.useOTP && !this.otpSent) {
      return !this.loginForm.get('emailOrMobile')?.invalid;
    }

    if (this.useOTP && this.otpSent) {
      return !(
        this.loginForm.get('emailOrMobile')?.invalid ||
        this.loginForm.get('otp')?.invalid
      );
    }

    return !this.loginForm.invalid;
  }

  private prepareCredentials(password: string, otp: string) {
    return this.useOTP
      ? { loginPassword: '', loginOTP: this.otpSent ? otp || '' : '' }
      : { loginPassword: password || '', loginOTP: '' };
  }

  private handleLoginResponse(
    res: HttpResponse<any>,
    rememberMe: boolean,
    password: string,
    emailOrMobile: string
  ): void {

    const body = res.body;

    if (!body) {
      this.toastr.error('Invalid server response', 'Error!');
      return;
    }

    // OTP Flow
    if (body.otp_sent) {
      this.handleOtpSent(body);
      return;
    }

    // API Error
    if (body.error) {
      this.toastr.error(body.msg || 'Login failed', 'Error!');
      this.router.navigate(['/consumer/login']);
      return;
    }

    // Success - Store user data in localStorage including login_type
    this.storeSessionData(body, emailOrMobile);
     this.xmppService.connect();
    this.handleRedirect();

    // Remember Me
    this.handleRememberMe(rememberMe, password);
  }

  private handleOtpSent(body: any): void {
    this.otpSent = true;
    this.submitted = false;

    const destination = this.loginType === 'email' ? 'email' : 'mobile';
    this.toastr.success(
      body.msg || `OTP sent to your ${destination}`,
      'Success!'
    );

    this.startResendTimer();

    // setTimeout(() => {
    //   this.otpInputs?.toArray()[0]?.nativeElement.focus();
    // }, 100);
    // Focus first OTP input
    setTimeout(() => {
      this.otp1Input?.nativeElement.focus();
    }, 100);
  }

  private storeSessionData(body: any, emailOrMobile: string): void {
    const data = body.data;
    console.log('Storing session data:', data);

    sessionStorage.setItem('TOKEN', body.data.access_token);

    localStorage.setItem('TOKEN', body.data.access_token);
    localStorage.setItem('REFRESH_TOKEN', body.data.refresh_token)
    localStorage.setItem('jid', data.uid);
    localStorage.setItem('PK', data.pk);
    localStorage.setItem('first_name', data.first_name);
    localStorage.setItem('mobile', data.mobile);
    localStorage.setItem('last_name', data.last_name);
    localStorage.setItem('email', data.email);
    
    // T&C: Store acceptance flag from login response
    localStorage.setItem('requiresTermsAcceptance', JSON.stringify(data['requires_terms_acceptance']));
    
    // Store full_name (combining first_name and last_name)
    const fullName = `${data.first_name} ${data.last_name}`.trim();
    localStorage.setItem('full_name', fullName);
    
    // Store login_type based on the type of login used
    localStorage.setItem('login_type', this.loginType);
    
    // Store the email or mobile based on login_type
    if (this.loginType === 'email') {
      localStorage.setItem('email', emailOrMobile);
    } else if (this.loginType === 'mobile') {
      localStorage.setItem('mobile', emailOrMobile);
    }

  }

  private handleRedirect(): void {
    const checkout = localStorage.getItem('checkoutLog');
    const token = sessionStorage.getItem('TOKEN');
    const redirect = this.route.snapshot.queryParams['redirect'];

    if (checkout && token && redirect === 'checkout') {
      this.router.navigate(['/consumer/checkout']);
    } else {
      this.router.navigate(['/consumer/appointments']);
    }
  }

  private handleRememberMe(
    rememberMe: boolean,
    password: string
  ): void {
    if (rememberMe && !this.useOTP) {
      localStorage.setItem('remembered_email_or_mobile', this.currentEmailOrMobile);
      localStorage.setItem('remembered_password', password);
      localStorage.setItem('remembered_checked', 'true');
    } else {
      localStorage.removeItem('remembered_email_or_mobile');
      localStorage.removeItem('remembered_password');
      localStorage.removeItem('remembered_checked');
    }
  }

  private handleLoginError(error: any): void {
    console.error('Login error:', error);
    this.toastr.error('Login failed. Please try again.', 'Error!');
  }
  
  // OTP Box Navigation Methods
  onOtpKeyUp(event: any, index: number, nextInput: any) {
    // Backspace/arrows are handled on keydown (onOtpKeyDown); ignoring them
    // here keeps this handler from pulling focus forward again.
    if (event.key === 'Backspace' || event.key === 'Delete' || event.key?.startsWith('Arrow')) {
      return;
    }

    const input = event.target;
    const value = input.value;

    // Allow only numbers
    if (value && !/^\d$/.test(value)) {
      this.otpDigits[index - 1] = '';
      input.value = '';
      return;
    }

    // Update the digit in array
    this.otpDigits[index - 1] = value;

    // **FIX: Update form control with combined OTP value**
    const otpValue = this.otpDigits.join('');
    this.loginForm.patchValue({ otp: otpValue });

    // Move to next input if value entered
    if (value && nextInput) {
      nextInput.focus();
    }
  }
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  restricttextSpace(event: any) {
    let inputValue: string = event.target.value;
    inputValue = inputValue.replace(/^\s+/, '');
    inputValue = inputValue.replace(/\s{2,}/g, ' ');
    event.target.value = inputValue;
    return;
  }

  private trimFormFields(): void {
    const emailOrMobileControl = this.loginForm.get('emailOrMobile');
    if (emailOrMobileControl) {
      emailOrMobileControl.setValue(emailOrMobileControl.value?.trim() ?? '');
    }
  }
}